import type { EnvelopeIcon } from '@heroicons/react/24/outline';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import * as React from 'react';
import { cn } from '../../lib/utils.ts';
import SmallSpinner from './small-spinner.tsx';

type Icon = typeof EnvelopeIcon;

export const inputVariants = cva(
    [
        'border-input ring-offset-background placeholder:text-muted-foreground',
        'flex w-full rounded-md border bg-transparent text-base',
        'file:border-0 file:bg-transparent file:text-sm file:font-medium',
        'focus-visible:outline-hidden',
        'hover:border-neutral-400',
        'active:border-blue-500 active:ring-blue-200 active:ring-1',
        'focus:border-blue-600 focus:ring-blue-200 focus:ring-1',
        'disabled:cursor-not-allowed disabled:opacity-50',
    ],
    {
        variants: {
            variant: {
                default: 'bg-white',
                inline: [
                    'border border-transparent', // No visible border by default
                    'bg-transparent', // Transparent until hover/focus
                    'hover:bg-cool-gray-50 hover:border-transparent', // Slight background on hover
                    'focus:bg-cool-gray-50 focus:border-blue-600', // Focus ring
                    'placeholder:text-neutral-600', // Placeholder style
                ],
            },
            size2: {
                default: 'h-8 px-2 py-2 sm:text-sm',
                sm: 'h-6 px-2.5 sm:text-sm',
            },
            hasLeftIcon: {
                true: 'pl-7',
            },
            hasRightIcon: {
                true: 'pr-7',
            },
        },
        compoundVariants: [
            {
                hasLeftIcon: true,
                size2: 'sm',
                class: 'pl-9',
            },
        ],
        defaultVariants: {
            variant: 'default',
            size2: 'default',
        },
    },
);

export type InputProps = {
    leftIcon?: Icon;
    rightIcon?: Icon;
    loading?: boolean;
    onRightIconClick?: () => void;
} & React.InputHTMLAttributes<HTMLInputElement> &
    VariantProps<typeof inputVariants>;

const Input = React.forwardRef<HTMLInputElement, InputProps>(
    (
        {
            className,
            leftIcon: LeftIcon,
            rightIcon: RightIcon,
            onRightIconClick = undefined,
            size2,
            type,
            loading,
            disabled,
            variant,
            ...props
        },
        ref,
    ) => {
        const combinedClassName = cn(
            inputVariants({ size2, hasLeftIcon: !!LeftIcon, hasRightIcon: !!RightIcon, variant }),
            {},
        );

        return (
            <div className={cn('relative flex w-full rounded-md h-8', className)}>
                {LeftIcon ? (
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2">
                        <LeftIcon className="h-4 w-4 text-neutral-400" aria-hidden="true" />
                    </div>
                ) : null}
                <input
                    disabled={disabled || loading}
                    type={type}
                    className={combinedClassName}
                    ref={ref}
                    {...props}
                />
                {RightIcon ? (
                    <div
                        className={cn(
                            'absolute inset-y-0 right-0 flex items-center pr-2',
                            onRightIconClick ? 'cursor-pointer' : 'pointer-events-none',
                        )}
                        onClick={onRightIconClick}
                    >
                        <RightIcon className="h-4 w-4 text-neutral-400" aria-hidden="true" />
                    </div>
                ) : null}
                {loading && (
                    <div className="absolute left-0 top-0 flex h-full w-full items-center justify-center rounded-md bg-neutral-100/70">
                        <SmallSpinner className="opacity-100" />
                    </div>
                )}
            </div>
        );
    },
);
Input.displayName = 'Input';

export { Input };
